'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'

const STORAGE_KEY = 'cookie_consent'

type ConsentValue = 'granted' | 'denied' | null

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

function updateGtagConsent(value: 'granted' | 'denied') {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: value,
      ad_storage: value,
    })
  }
}

const content = {
  message: 'Ми використовуємо cookies для аналізу трафіку та покращення сайту.',
  accept: 'Прийняти',
  decline: 'Відхилити',
  privacyLabel: 'Детальніше',
  privacyHref: '/privacy-policy',
} as const

export function CookieConsent() {
  const [consent, setConsent] = useState<ConsentValue>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ConsentValue
    if (stored === 'granted' || stored === 'denied') {
      setConsent(stored)
      updateGtagConsent(stored)
    } else {
      setVisible(true)
    }
  }, [])

  function handleAccept() {
    localStorage.setItem(STORAGE_KEY, 'granted')
    setConsent('granted')
    updateGtagConsent('granted')
    setVisible(false)
  }

  function handleDecline() {
    localStorage.setItem(STORAGE_KEY, 'denied')
    setConsent('denied')
    updateGtagConsent('denied')
    setVisible(false)
  }

  if (!visible || consent !== null) return null

  const t = content

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Сповіщення про cookies"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
    >
      <div className="max-w-2xl mx-auto bg-deep text-cream rounded-2xl shadow-2xl shadow-black/40 px-5 py-4 md:px-6 md:py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <p className="font-montserrat-alt text-xs md:text-sm text-cream/80 flex-1 leading-relaxed">
          {t.message}{' '}
          <Link
            href={t.privacyHref}
            className="underline underline-offset-2 text-cream/60 hover:text-cream transition-colors duration-200"
          >
            {t.privacyLabel}
          </Link>
          .
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleDecline}
            className="font-montserrat-alt text-xs font-semibold px-4 py-2 rounded-full border border-cream/20 text-cream/60 hover:text-cream hover:border-cream/40 transition-all duration-200"
          >
            {t.decline}
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="font-montserrat-alt text-xs font-semibold px-4 py-2 rounded-full bg-rose text-deep hover:bg-cream transition-all duration-200"
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  )
}
